<template>
  <div class="ml-56 h-screen overflow-y-scroll lg:overflow-y-hidden">
    <!-- Loading -->
    <div
      v-if="isLoading"
      class="w-full h-screen flex items-center justify-center bg-truegray-100"
    >
      <div
        v-if="isLoading"
        class="h-52 flex flex-col justify-center items-center"
      >
        <lottie :options="loadingAnimationOptions" />
        <span class="text-center">Loading CareerWise data</span>
      </div>
    </div>
    <!-- /Loading -->

    <!-- Content -->
    <div v-else class="flex flex-wrap lg:flex-nowrap lg:h-full">
      <!-- Stats -->
      <div class="relative h-full w-full bg-truegray-100 lg:overflow-y-scroll">
        <PageHeader
          :overline="headerConfig.overline"
          :title="headerConfig.title"
        >
          <template #title>
            <div class="flex items-center space-x-4 w-3/4">
              <div
                class="rounded-full flex items-center justify-center border-2 w-14 h-14 relative flex-shrink-0 contributor-badge-cw"
              >
                <div
                  v-if="organisation?.logo"
                  class="bg-no-repeat bg-cover bg-center w-12 h-12 rounded-full"
                  :style="{ backgroundImage: 'url(' + organisation.logo + ')' }"
                />
                <div
                  v-else
                  class="flex justify-center items-center w-8 h-8 rounded-full text-cw-900"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-8 w-8"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>

                <div
                  :style="[{ top: '-0.5rem' }, { left: '2.3rem' }]"
                  class="absolute border-2 border-white cw-gradient h-5 w-5 p-1 rounded-full text-white"
                >
                  <div class="relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-2 w-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div class="flex flex-col justify-center">
                <h1 class="text-cw-900 text-3xl font-montserrat m-0 font-bold">
                  {{ organisation?.title || headerConfig.title }}
                </h1>
              </div>
            </div>
          </template>
        </PageHeader>

        <div class="p-12 space-y-4">
          <div class="grid grid-cols-1 xl:grid-cols-6 gap-4">
            <StatisticsCardCounter
              type="posts"
              :title="postCounterTitle"
              getter="contributors/getPostCounter"
              modal-name="cw-contributor-post-pack"
              class="xl:col-span-2"
            />

            <StatisticsCard
              title="Total Student Reach"
              class="xl:col-span-2"
              content-classes="py-4"
            >
              <template #content>
                <img
                  :style="{ maxWidth: '125px' }"
                  src="/img/icons/contributor_dashboard/graduation-cap-gradient.svg"
                  alt="Graduation cap icon"
                />

                <h3 class="font-montserrat cw-gradient-text">
                  {{ studentReach }}K
                </h3>
              </template>
            </StatisticsCard>

            <StatisticsCard
              title="Our Total Schools"
              class="xl:col-span-2"
              content-classes="pt-8 pb-4"
            >
              <template #content>
                <img
                  :style="{ maxWidth: '125px' }"
                  src="/img/icons/contributor_dashboard/school-building-gradient.svg"
                  alt="School building icon"
                />

                <div class="flex pt-8 w-full items-center justify-center gap-6">
                  <h3 class="font-montserrat m-0">{{ cwSchoolsCount }}</h3>
                  <button
                    class="btn m-0 py-1 px-2 text-white bg-truegray-300 hover:bg-black font-nunito font-normal"
                    @click="showSchoolsModal"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-3 h-3 mr-2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    List
                  </button>
                </div>
              </template>
            </StatisticsCard>

            <StatisticsCard
              title="Total Mailouts"
              class="xl:col-span-3"
              content-classes="pt-8"
            >
              <template #content>
                <div class="flex w-full items-center justify-center gap-6">
                  <div class="flex p-4 rounded-full cw-gradient text-white">
                    <img
                      :style="{ maxWidth: '115px' }"
                      src="/img/icons/contributor_dashboard/send-email-white.svg"
                      alt="Mail icon"
                    />
                  </div>
                  <h3 class="font-montserrat m-0">
                    {{ formatNumber(cwMailouts) }}
                  </h3>
                </div>
                <!-- @TODO turn this back on after we have a long enough period to query time periods without the estimated addition from may 2022 to october 2023 -->
                <!--
                  <InputDropdownSelector
                  :type="'MAILOUTS'"
                  :wrapper-classes="'mt-6'"
                  /> 
                -->
              </template>
            </StatisticsCard>

            <StatisticsCardPostCategories class="xl:col-span-3" />
          </div>
        </div>
      </div>
      <!-- /Stats -->
    </div>
    <!-- /Content -->
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { defineComponent, computed, ref, watch, onMounted } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { formatNumber } from '../../../js/util/helper.js'
import PageHeader from '../components/PageHeader.vue'
import StatisticsCard from '../components/StatisticsCard.vue'
import StatisticsCardPostCategories from '../components/StatisticsCardPostCategories.vue'
import StatisticsCardCounter from '../components/StatisticsCardCounter.vue'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'

export default defineComponent({
  name: 'Home',

  components: {
    PageHeader,
    StatisticsCard,
    StatisticsCardPostCategories,
    StatisticsCardCounter,
  },

  setup() {
    const store = useStore()

    const headerConfig = {
      overline: 'organisation',
      title: 'Your Organisation',
    }

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)
    const globalLoading = computed(() => store.getters['shared/isLoading'])
    watch(globalLoading, (val) => {
      isLoading.value = val
    })

    const postCounter = computed(() => {
      return store.getters['contributors/getPostCounter']
    })

    // This is duplicated in statistics.vue @todo - move to store?
    const postCounterTitle = computed(() => {
      return postCounter.value?.subscription
        ? 'Monthly Post Count'
        : 'Post Count'
    })

    const organisation = computed(() => {
      return store.getters['contributors/getOrganisation']
    })

    const studentReach = computed(() => {
      return store.getters['contributors/getStudentReach']
    })

    const cwSchoolsCount = computed(() => {
      return store.getters['contributors/getSchoolsCount']
    })

    const showSchoolsModal = () => {
      EventBus.$emit('showModal', {
        action: 'cw-contributor-schools',
      })
    }

    const cwMailouts = computed(() => {
      return store.getters['contributors/getMailouts']
    })

    const init = async () => {
      isLoading.value = true
      await store.dispatch('contributors/dispatchInitPageDashboard')
      isLoading.value = false
    }

    onMounted(init)

    return {
      loadingAnimationOptions,
      headerConfig,
      isLoading,
      postCounterTitle,
      organisation,
      studentReach,
      cwSchoolsCount,
      cwMailouts,
      showSchoolsModal,
      formatNumber,
    }
  },
})
</script>

<style scoped>
@media screen and (min-width: 1024px) {
  .contributor-top-posts {
    width: calc((100vw - 14rem) / 2);
  }
}
</style>
